import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InputSelectMulti } from 'components/InputSelect/InputSelect';
import SpinnerComponent from 'components/Spinner/SpinnerComponent';
import { useGlobalContext } from 'context/GlobalContext';
import environment from 'constants/apiConst';
import Swal from 'sweetalert2';

const OPERATIONS_LIST = [
    { id: 'auth_ingreso', label: 'Autorizacion de Ingreso' },
    { id: 'auth_salida', label: 'Autorizacion de Salida' },
    { id: 'auth_ci', label: 'Autorizacion de Cuadro de Integracion' },
    { id: 'auth_declaracion', label: 'Autorizacion de Declaracion' },
    { id: 'auth_pase_entrada', label: 'Autorizacion Pase Entrada' },
    { id: 'auth_pase_salida', label: 'Autorizacion Pase Salida' },
    { id: 'corr_ingreso', label: 'Correccion de Ingreso' },
    { id: 'corr_salida', label: 'Correccion de Salida' },
    { id: 'corr_ci', label: 'Correccion de Cuadro de Integracion' },
    { id: 'rev_declaracion', label: 'Reversion de Declaracion' },
    { id: 'rev_pase_entrada', label: 'Reversion Pase Entrada' },
    { id: 'rev_pase_salida', label: 'Reversion Pase Salida' },
    { id: 'anul_ingreso', label: 'Anulacion de Ingreso' },
    { id: 'anul_salida', label: 'Anulacion de Salida' },
    { id: 'anul_ci', label: 'Anulacion de Cuadro de Integracion' },
    { id: 'pesajes_realizados', label: 'Pesajes realizados' },
    { id: 'pesajes_completos', label: 'Pesajes completos' }
];

export const EstadisticasOperacionesGeneral = () => {
    const { requestData } = useGlobalContext();
    const [loadingExport, setLoadingExport] = useState(false);

    const [selectedFreeZones, setSelectedFreeZones] = useState([]);
    const [selectedOperations, setSelectedOperations] = useState(OPERATIONS_LIST.map(op => op.id));

    const { isLoading: isLoadingFZ, data: dataFZ } = useQuery({
        queryKey: ['GET_ALL_FREE_ZONES_LOGIN'],
        queryFn: () => requestData({
            Endpoint: environment.GET_ALL_FREE_ZONES_LOGIN,
        })
    });

    const freeZonesOptions = !isLoadingFZ && dataFZ?.data?.status?.data
        ? dataFZ.data.status.data.map((item) => ({
            value: item.id,
            label: item.name_freeZone || item.shortName_freeZone,
            ...item,
        }))
        : [];

    const handleCheckboxChange = (opId) => {
        setSelectedOperations((prev) =>
            prev.includes(opId)
                ? prev.filter(id => id !== opId)
                : [...prev, opId]
        );
    };

    const handleSelectAllOperations = () => {
        if (selectedOperations.length === OPERATIONS_LIST.length) {
            setSelectedOperations([]);
        } else {
            setSelectedOperations(OPERATIONS_LIST.map(op => op.id));
        }
    };

    const handleExport = async () => {
        if (selectedFreeZones.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Faltan datos',
                text: 'Por favor seleccione al menos una Zona Franca antes de exportar.',
                confirmButtonColor: '#0d6efd'
            });
            return;
        }
        if (selectedOperations.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Faltan datos',
                text: 'Por favor seleccione al menos un tipo de operación.',
                confirmButtonColor: '#0d6efd'
            });
            return;
        }

        setLoadingExport(true);
        try {
            const payload = {
                type_export_file: "xlsx",
                free_zones: selectedFreeZones.map(fz => fz.value),
                operations: selectedOperations
            };

            await requestData({
                method: 'POST',
                Endpoint: environment.EXPORT_STATISTICS_GENERAL_OP,
                data: payload
            });

            Swal.fire({
                icon: 'success',
                title: '¡Reporte en proceso!',
                html: 'Su informe de estadísticas generales se está generando en segundo plano.<br><br><b>En un momento recibirá una notificación</b> en la plataforma cuando el archivo esté listo para su descarga.',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#0d6efd'
            });

        } catch (error) {
            console.error("Error al exportar:", error);
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Hubo un error en el servidor al intentar generar la exportación. Por favor, intente de nuevo.',
                confirmButtonColor: '#0d6efd'
            });
        } finally {
            setLoadingExport(false);
        }
    };

    return (
        <div className="container bg-white p-4 rounded shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Estadísticas operaciones Global</h2>
                <button
                    className="btn btn-primary"
                    onClick={handleExport}
                    disabled={loadingExport || isLoadingFZ}
                >
                    {loadingExport ? 'Procesando...' : 'Exportar Excel'}
                </button>
            </div>

            <hr />

            <div className="row mt-4">
                <div className="col-md-12 mb-4">
                    <label className="fw-bold mb-2">1. Seleccione Zonas Francas</label>
                    <InputSelectMulti
                        tag={"Zonas Francas"}
                        name={"freeZones"}
                        data={freeZonesOptions}
                        set={setSelectedFreeZones}
                        value={selectedFreeZones}
                        isLoading={isLoadingFZ}
                        isDisabled={isLoadingFZ}
                    />
                </div>

                <div className="col-md-12 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="fw-bold">2. Seleccione Tipos de Operación</label>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleSelectAllOperations}
                        >
                            {selectedOperations.length === OPERATIONS_LIST.length
                                ? 'Deseleccionar todos'
                                : 'Seleccionar todos'
                            }
                        </button>
                    </div>

                    <div className="row border rounded p-3 bg-light m-0">
                        {OPERATIONS_LIST.map((op) => (
                            <div className="col-md-4 col-sm-6 mb-2" key={op.id}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`check_${op.id}`}
                                        checked={selectedOperations.includes(op.id)}
                                        onChange={() => handleCheckboxChange(op.id)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <label
                                        className="form-check-label user-select-none"
                                        htmlFor={`check_${op.id}`}
                                        style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                    >
                                        {op.label}
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            {(loadingExport || isLoadingFZ) && <SpinnerComponent />}
        </div>
    );
};

export default EstadisticasOperacionesGeneral;